// ─── Workshop Truck Availability Tracker ───
// TruckDetailModal – Full detail view with two-column layout + status history
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import { useState, useEffect } from 'react'
import { Truck as TruckIcon, X, Loader2 } from 'lucide-react'
import type { Truck, StatusHistoryEntry, WorkshopStatus, RequiredPart } from '@/types'
import StatusBadge from '@/components/ui/StatusBadge'
import {
  STATUS_COLORS,
  calcDaysInWorkshop,
  getDaysColor,
  formatDate,
  formatDateTime,
  normalizeMake,
} from '@/utils/helpers'
import { api } from '@/utils/api'

// ─── Props ──────────────────────────────────────────────────

interface TruckDetailModalProps {
  truck: Truck | null
  onClose: () => void
  onEdit: () => void
}

// ─── Client colour map ──────────────────────────────────────

const CLIENT_BADGE_COLORS: Record<string, string> = {
  LAPW: 'bg-blue-100 text-blue-800',
  TSL: 'bg-green-100 text-green-800',
  LAPE: 'bg-purple-100 text-purple-800',
  TNP: 'bg-orange-100 text-orange-800',
}

function getClientBadgeClass(name: string): string {
  const key = Object.keys(CLIENT_BADGE_COLORS).find((k) =>
    name.toUpperCase().includes(k),
  )
  return key ? CLIENT_BADGE_COLORS[key] : 'bg-gray-100 text-gray-700'
}

// ─── Info Row helper ────────────────────────────────────────

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-2">
      <span className="text-xs text-gray-500 uppercase tracking-wide font-medium min-w-[110px]">
        {label}
      </span>
      <span className="text-sm text-gray-800 text-right max-w-[60%]">{children}</span>
    </div>
  )
}

// ─── Component ──────────────────────────────────────────────

export default function TruckDetailModal({ truck, onClose, onEdit }: TruckDetailModalProps) {
  const [history, setHistory] = useState<StatusHistoryEntry[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  // Fetch status history when truck changes
  useEffect(() => {
    if (!truck) {
      setHistory([])
      return
    }

    let cancelled = false
    const fetchHistory = async () => {
      setHistoryLoading(true)
      try {
        const res = await api.get<{
          success: boolean
          data: { truck: Truck; history: StatusHistoryEntry[] }
        }>(`/api/trucks/${truck.asset_id}`)
        if (!cancelled) {
          setHistory(res.data?.history ?? [])
        }
      } catch {
        if (!cancelled) setHistory([])
      } finally {
        if (!cancelled) setHistoryLoading(false)
      }
    }

    fetchHistory()
    return () => { cancelled = true }
  }, [truck?.asset_id])

  if (!truck) return null

  const days = calcDaysInWorkshop(truck.workshop_entry_date)
  const daysColor = getDaysColor(days)

  // Parse required jobs into list
  const jobList = truck.required_jobs
    ? truck.required_jobs
      .split(',')
      .map((j) => j.trim())
      .filter(Boolean)
    : []

  // Parse required parts
  let parsedParts: Record<string, RequiredPart[]> = {}
  if (truck.required_parts) {
    try {
      parsedParts = JSON.parse(truck.required_parts)
    } catch (e) {
      console.error('Failed to parse required_parts', e)
    }
  }
  const hasParts = Object.keys(parsedParts).length > 0

  let totalCost = 0
  if (hasParts) {
    Object.values(parsedParts).forEach(items => {
      items.forEach(item => {
        totalCost += (item.estimated_cost || 0)
      })
    })
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto z-50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ═══ HEADER ════════════════════════════════════════ */}
        <div className="bg-[#1E2A3A] px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TruckIcon size={22} className="text-white" />
            <span className="text-white text-xl font-bold">{truck.registration}</span>
            <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${getClientBadgeClass(truck.client_name)}`}>
              {truck.client_name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="bg-[#3B82F6] hover:bg-blue-600 text-white text-sm px-3 py-1.5 rounded-lg transition-colors"
            >
              Update Status
            </button>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* ═══ BODY ══════════════════════════════════════════ */}
        <div className="px-6 py-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ── LEFT COLUMN — Asset Information ──────────── */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 pb-2 border-b border-gray-100">
                Asset Information
              </h3>
              <div className="divide-y divide-gray-50">
                <InfoRow label="Fleet #">{truck.registration}</InfoRow>
                <InfoRow label="Description">{truck.description || '—'}</InfoRow>
                <InfoRow label="Client">{truck.client_name}</InfoRow>
                <InfoRow label="Fleet Number">{truck.fleet_number || '—'}</InfoRow>
                <InfoRow label="Asset Type">{truck.asset_type || '—'}</InfoRow>
                <InfoRow label="Make">{normalizeMake(truck.make)}</InfoRow>
                <InfoRow label="Model">{truck.model || '—'}</InfoRow>
                <InfoRow label="User State">{truck.user_state || '—'}</InfoRow>
                <InfoRow label="Odometer">
                  {truck.odometer != null ? `${truck.odometer.toLocaleString()} km` : '—'}
                </InfoRow>
                <InfoRow label="Speed">
                  {truck.speed_kmh != null ? `${truck.speed_kmh} km/h` : '—'}
                </InfoRow>
                <InfoRow label="Current Address">
                  <span className="text-sm break-words">
                    {truck.current_address || '—'}
                  </span>
                </InfoRow>
                <InfoRow label="GPS">
                  {truck.current_lat && truck.current_lon ? (
                    <span className="font-mono text-xs text-gray-500">
                      {truck.current_lat}, {truck.current_lon}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </InfoRow>
                <InfoRow label="Last Synced">
                  {formatDateTime(truck.record_synced_at)}
                </InfoRow>
              </div>
            </div>

            {/* ── RIGHT COLUMN — Workshop Information ──────── */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 pb-2 border-b border-gray-100">
                Workshop Information
              </h3>
              <div className="divide-y divide-gray-50">
                <InfoRow label="Workshop">
                  {truck.workshop_location ? (
                    <span className="font-medium text-gray-800">{truck.workshop_location}</span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </InfoRow>
                <InfoRow label="SLA">
                  {truck.sla_status ? (
                    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                      truck.sla_status === 'SLA' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {truck.sla_status}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </InfoRow>
                <InfoRow label="Status">
                  <StatusBadge status={truck.workshop_status} size="md" />
                </InfoRow>
                <InfoRow label="Available">
                  {truck.is_available === 1 ? (
                    <span className="text-green-700 font-medium">✅ Available</span>
                  ) : (
                    <span className="text-amber-700 font-medium">🔧 In Workshop</span>
                  )}
                </InfoRow>
                <InfoRow label="Entry Date">
                  {formatDate(truck.workshop_entry_date)}
                </InfoRow>
                <InfoRow label="Days In Workshop">
                  <span className={daysColor}>
                    {days !== null ? `${days} day${days !== 1 ? 's' : ''}` : '—'}
                  </span>
                </InfoRow>
                <InfoRow label="Est. Release">
                  {formatDate(truck.estimated_release_date)}
                </InfoRow>

                {/* Required Jobs */}
                <div className="py-2">
                  <span className="text-xs text-gray-500 uppercase tracking-wide font-medium block mb-1.5">
                    Required Jobs
                  </span>
                  {jobList.length === 0 ? (
                    <span className="text-sm text-gray-400 italic">None recorded</span>
                  ) : (
                    <ul className="space-y-1 ml-1">
                      {jobList.map((job, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="text-gray-400 mt-1.5 text-[6px]">●</span>
                          {job}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Required Parts */}
                <div className="py-2">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-gray-500 uppercase tracking-wide font-medium block">
                      Required Parts
                    </span>
                    {hasParts && (
                      <span className="text-xs font-bold text-gray-800 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                        Total: ₦{totalCost.toLocaleString()}
                      </span>
                    )}
                  </div>
                  {!hasParts ? (
                    <span className="text-sm text-gray-400 italic">None recorded</span>
                  ) : (
                    <div className="space-y-2 mt-2">
                      {Object.entries(parsedParts).map(([cat, items]) => (
                        <div key={cat} className="text-sm">
                          <span className="font-medium text-[#1E2A3A] text-[10px] uppercase tracking-wide bg-gray-100 px-1.5 py-0.5 rounded">{cat}</span>
                          <ul className="mt-1 space-y-1">
                            {items.map((item, i) => (
                              <li key={i} className="flex items-center justify-between text-gray-700 bg-white border border-gray-100 px-2 py-1.5 rounded shadow-sm">
                                <span className="text-xs">{item.description}</span>
                                <span className="font-mono text-green-700 font-medium text-xs">₦{(item.estimated_cost || 0).toLocaleString()}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div className="py-2">
                  <span className="text-xs text-gray-500 uppercase tracking-wide font-medium block mb-1.5">
                    Notes
                  </span>
                  {!truck.workshop_notes ? (
                    <span className="text-sm text-gray-400">—</span>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                      {truck.workshop_notes}
                    </div>
                  )}
                </div>

                <InfoRow label="Last Updated">
                  {formatDateTime(truck.last_status_update)}
                </InfoRow>
                <InfoRow label="Updated By">
                  {truck.updated_by || '—'}
                </InfoRow>
              </div>
            </div>
          </div>

          {/* ═══ STATUS HISTORY TIMELINE ═══════════════════ */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <h3 className="font-semibold text-gray-700 mb-3 mt-2">
              Status History (Last 10 Updates)
            </h3>

            {historyLoading ? (
              <div className="flex items-center justify-center py-8 text-gray-400 gap-2">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-sm">Loading history…</span>
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-4">
                No status history available.
              </p>
            ) : (
              <div className="relative pl-6 border-l-2 border-gray-200">
                {history.map((entry) => {
                  const dotColor =
                    STATUS_COLORS[entry.status as WorkshopStatus] ?? '#6B7280'
                  return (
                    <div key={entry.id} className="mb-4 relative">
                      {/* Timeline dot */}
                      <div
                        className="absolute -left-[25px] w-3 h-3 rounded-full border-2 border-white shadow-sm"
                        style={{ backgroundColor: dotColor }}
                      />
                      {/* Content */}
                      <div className="flex justify-between items-start">
                        <div>
                          <StatusBadge
                            status={entry.status as WorkshopStatus}
                            size="sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            by {entry.updated_by}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap ml-4">
                          {formatDateTime(entry.recorded_at)}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* ═══ FOOTER ════════════════════════════════════════ */}
        <div className="border-t border-gray-100 px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm
                       hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          <button
            onClick={onEdit}
            className="px-4 py-2 bg-[#3B82F6] text-white rounded-lg text-sm
                       hover:bg-blue-600 transition-colors"
          >
            ✏️ Update Status
          </button>
        </div>
      </div>
    </div>
  )
}
